import type { ReactNode } from 'react';
import type { AlignYTypes, SpacingTypes } from '@etg/wings';
import { Inline, Stack, vars, Text } from '@etg/wings';
import { css, cx, mediaQueries } from '@eti/styles';
import { useSiteContext, useTranslation } from '@eti/providers';
import { constants, date, duration as durationUtil } from '@eti/utils';
import { getStopoverType } from '../../../../../../../utils/format';
import { SelfTransferIndicator, StopoverInfoIndicator } from '../TripIndicator';
import { TripDetailsBoundCarrierLogo } from '../TripDetailsBoundCarrierLogo';
import { TripDetailsLocationTimeAndDate } from '../TripDetailsLocationTimeAndDate';
import { TRAIN_SEGMENTS } from '../../constants';
import {
  getBoundTitle,
  getBoundType,
} from '../../../../../../../widgets/trip-details/utils/format';
import type { TripDetailsBoundProps } from './model';
import useGenerateTripBoundAriaLabel from './hooks/useGenerateTripBoundAriaLabel';

export const tripDetailsLocationTimeAndDateWrapperCss = css`
  width: fit-content;
`;

const boundInlineStyles = css`
  cursor: pointer;
  padding: 8px 16px;
`;

const columnStyles = (alignStart?: boolean) => css`
  align-items: ${alignStart ? 'start' : 'center'};
  display: flex;
  justify-content: space-between;
`;

const stackStyles = css`
  width: calc(100% - 38px);
`;

const technicalStopStyles = css`
  font-size: 0.875rem;
  font-weight: 400;
`;

const trainAndTechnicalStopStyles = css`
  text-align: end;
`;

interface SeparatorStyledProps {
  children: ReactNode;
  isLightSeparator?: boolean;
}

const separatorStyles = (isLightSeparator?: boolean) => css`
  ${isLightSeparator &&
  css`
    color: #737373;
  `}
`;

export const SeparatorStyled = ({ children, isLightSeparator, ...rest }: SeparatorStyledProps) => (
  <div className={separatorStyles(isLightSeparator)} {...rest}>
    {children}
  </div>
);

const infoTextStyles = (bold?: boolean) => css`
  font-size: 0.875rem;
  font-weight: ${bold ? 700 : 400};

  @media ${mediaQueries.small.only} {
    text-wrap: nowrap;
  }
`;

interface InfoTextStyledProps {
  bold?: boolean;
  children: ReactNode;
  className?: string;
}

export const InfoTextStyled = ({ bold, children, className, ...rest }: InfoTextStyledProps) => (
  <p className={cx(infoTextStyles(bold), className)} {...rest}>
    {children}
  </p>
);

interface StyledAirportInlineProps {
  alignY?: AlignYTypes;
  children: ReactNode;
  className?: string;
  spacing?: SpacingTypes;
}

const airportInlineStyles = css`
  color: #737373;
  font-size: 0.875rem;
`;

export const StyledAirportInline = ({ children, className, ...rest }: StyledAirportInlineProps) => (
  <Inline className={cx(airportInlineStyles, className)} {...rest}>
    {children}
  </Inline>
);

const nearbyAirportStyles = css`
  background: ${vars.colors.signal.caution.background};
  color: ${vars.colors.signal.caution.foreground};
  padding: 0 4px;
`;

const nearbyAirportTextStyles = css`
  color: #737373;
`;

const { unicodeControlCharacters } = constants;
const { LRE, PDF } = unicodeControlCharacters;

const TripDetailsBoundSmall = ({
  boundAsSegment,
  boundNumber,
  flightId,
  includesTrainSegments,
  marketingCarriers,
  segments,
  onClick,
  differentDestinationAirportCode,
  differentOriginAirportCode,
  isMultiStop,
  isVISelfTransferDealEnabled,
}: TripDetailsBoundProps) => {
  const { t } = useTranslation();
  const { language, timeFormat } = useSiteContext();
  const stopoverType = getStopoverType(segments);
  const title = t(getBoundTitle(getBoundType(boundNumber - 1), isMultiStop), boundNumber);
  const { arrivedAt, departuredAt, destination, duration, isSelfTransfer, origin, stops } =
    boundAsSegment;
  const isDirectFlight = stops === 0;
  const stopText =
    stops === 1 ? t('TripDetails.Details.Stop') : t('TripDetails.Details.Stops', stops);
  const localizedDuration = durationUtil.getDuration(
    duration,
    language.code,
    timeFormat,
    ['h', 'm', 's'],
    false,
  );
  const numberOfTechnicalStops =
    segments
      ?.filter((segment) => segment.__typename === 'TripSegment')
      .reduce((acc, segment) => {
        if (Number.isFinite(segment.numberOfTechnicalStops)) {
          return acc + segment.numberOfTechnicalStops;
        }

        return acc;
      }, 0) ?? 0;

  const tripBoundAriaLabel = useGenerateTripBoundAriaLabel(
    boundAsSegment,
    marketingCarriers,
    title,
    includesTrainSegments,
    numberOfTechnicalStops,
  );

  return (
    <Inline
      alignY="top"
      aria-label={tripBoundAriaLabel}
      className={cx(boundInlineStyles, 'target_class')}
      data-testid={`resultPage-resultTrip-${flightId}-bound-${boundNumber}-wrapper`}
      onClick={onClick}
      spacing={8}
    >
      <TripDetailsBoundCarrierLogo marketingCarriers={marketingCarriers} />
      <Stack aria-hidden className={stackStyles} spacing={4}>
        <div className={columnStyles()}>
          <Inline alignY="center" className={tripDetailsLocationTimeAndDateWrapperCss} spacing={4}>
            <TripDetailsLocationTimeAndDate
              boundNumber={boundNumber}
              dateAndTime={departuredAt}
              type="departure"
            />
            <SeparatorStyled>&ndash;</SeparatorStyled>
            <TripDetailsLocationTimeAndDate
              boundNumber={boundNumber}
              dateAndTime={arrivedAt}
              durationInDays={date.durationInDays(new Date(departuredAt), new Date(arrivedAt))}
              type="arrival"
            />
          </Inline>
          <InfoTextStyled bold data-testid="searchResults-segment-stops">
            {isDirectFlight ? t('TripDetails.Details.Direct') : stopText}
          </InfoTextStyled>
        </div>
        <div className={columnStyles()}>
          <StyledAirportInline
            alignY="center"
            data-testid={`trip-bound-${boundNumber}-info-location`}
            spacing={4}
          >
            <Text className={differentOriginAirportCode ? nearbyAirportStyles : null}>
              {origin.airportCode ?? origin.code}
            </Text>
            {differentOriginAirportCode && (
              <Text className={nearbyAirportTextStyles}>
                {t(
                  'Result.Page.ResultCard.TripBound.NearbyAirport.Text',
                  differentOriginAirportCode,
                )}
              </Text>
            )}
            <SeparatorStyled isLightSeparator>&ndash;</SeparatorStyled>
            <Text className={differentDestinationAirportCode ? nearbyAirportStyles : null}>
              {destination.airportCode ?? destination.code}
            </Text>
            {differentDestinationAirportCode && (
              <Text className={nearbyAirportTextStyles}>
                {t(
                  'Result.Page.ResultCard.TripBound.NearbyAirport.Text',
                  differentDestinationAirportCode,
                )}
              </Text>
            )}
          </StyledAirportInline>
          <InfoTextStyled data-testid="searchResults-segment-duration">
            {`${LRE}${localizedDuration}${PDF}`}
          </InfoTextStyled>
        </div>
        <div className={columnStyles(isSelfTransfer || Boolean(stopoverType))}>
          <Inline spacing={4}>
            {isSelfTransfer && !isVISelfTransferDealEnabled && (
              <SelfTransferIndicator alignX="start" boundNumber={boundNumber} />
            )}
            {stopoverType && <StopoverInfoIndicator stopoverType={stopoverType} />}
          </Inline>
          <Stack className={trainAndTechnicalStopStyles} spacing={4}>
            {includesTrainSegments === TRAIN_SEGMENTS.SOME && (
              <InfoTextStyled data-testid="trip-part-by-train-text">
                {t('IbeClient.Segment.PartByTrain.Text')}
              </InfoTextStyled>
            )}
            {includesTrainSegments === TRAIN_SEGMENTS.ALL && (
              <InfoTextStyled data-testid="trip-by-train-text">
                {t('IbeClient.Segment.Train.Text')}
              </InfoTextStyled>
            )}
            {numberOfTechnicalStops > 0 && (
              <p className={technicalStopStyles} data-testid="trip-technical-stops">
                {t('TripDetails.Details.NumberOfTechnicalStops', numberOfTechnicalStops)}
              </p>
            )}
          </Stack>
        </div>
      </Stack>
    </Inline>
  );
};

export { TripDetailsBoundSmall };
